// src/pages/AdminNotificacoesArquivadas.jsx

import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Avatar,
  CircularProgress,
  Button,
  TextField,
  InputAdornment,
  alpha,
  useTheme,
  Divider,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArchiveIcon from "@mui/icons-material/Archive";
import SearchIcon from "@mui/icons-material/Search";
import RestoreIcon from "@mui/icons-material/Restore";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import HistoryIcon from "@mui/icons-material/History";
import BoltIcon from "@mui/icons-material/Bolt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import MarkEmailUnreadIcon from "@mui/icons-material/MarkEmailUnread";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import BusinessIcon from "@mui/icons-material/Business";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import PersonIcon from "@mui/icons-material/Person";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";

import {
  collection,
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
  doc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../contexts/firebase/config";

function showSuccess(message) {
  enqueueSnackbar(message, { variant: "success" });
}

function showError(message) {
  enqueueSnackbar(message, { variant: "error" });
}

function showWarning(message) {
  enqueueSnackbar(message, { variant: "warning" });
}

function parseDate(value) {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;

  const d = new Date(value);
  return isNaN(d) ? null : d;
}

function formatarData(value) {
  const d = parseDate(value);
  return d ? d.toLocaleString("pt-PT") : "—";
}

function formatarHora(value) {
  const d = parseDate(value);
  return d
    ? d.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" })
    : "—";
}

function money(value) {
  return `€ ${Number(value || 0).toFixed(2)}`;
}

function normalize(value) {
  return (value || "").toString().toLowerCase().trim();
}

function obterNomeLog(log) {
  return (
    log.clienteNome ||
    log.entidadeNome ||
    log.empresaNome ||
    log.metadata?.email ||
    "Sistema"
  );
}

function obterDetalheValor(log) {
  const partes = [];

  if (log.referencia) partes.push(`Referência: ${log.referencia}`);
  if (log.valor) partes.push(`Valor: ${money(log.valor)}`);
  if (log.empresaNome) partes.push(`Empresa: ${log.empresaNome}`);

  return partes.join(" · ");
}

function getLogUi(log) {
  const acao = (log.acao || "").toUpperCase();
  const tipo = (log.tipo || "").toUpperCase();

  if (acao.includes("PAGAMENTO_CONFIRMADO")) {
    return { title: "Pagamento confirmado", icon: <CheckCircleIcon />, color: "#16a34a" };
  }

  if (acao.includes("REVERTIDO")) {
    return { title: "Pagamento revertido", icon: <AutorenewIcon />, color: "#dc2626" };
  }

  if (acao.includes("EMAIL")) {
    return { title: "Email enviado", icon: <MarkEmailUnreadIcon />, color: "#2563eb" };
  }

  if (acao.includes("REFERENCIA") || acao.includes("REFERÊNCIA")) {
    return { title: "Referência gerada", icon: <CreditCardIcon />, color: "#d97706" };
  }

  if (acao.includes("CLIENTE_CRIADO")) {
    return { title: "Novo cliente criado", icon: <PersonIcon />, color: "#7c3aed" };
  }

  if (acao.includes("CLIENTE_ATUALIZADO")) {
    return { title: "Cliente atualizado", icon: <AutorenewIcon />, color: "#0ea5e9" };
  }

  if (acao.includes("CLIENTE_APAGADO")) {
    return { title: "Cliente removido", icon: <WarningAmberIcon />, color: "#dc2626" };
  }

  if (acao.includes("EMPRESA_CRIADA")) {
    return { title: "Empresa criada", icon: <BusinessIcon />, color: "#14b8a6" };
  }

  if (acao.includes("COMPRA_CRIADA")) {
    return { title: "Compra criada", icon: <ShoppingBagIcon />, color: "#f59e0b" };
  }

  if (tipo === "ERRO") {
    return { title: "Erro do sistema", icon: <WarningAmberIcon />, color: "#dc2626" };
  }

  return {
    title: log.titulo || "Atividade registada",
    icon: <BoltIcon />,
    color: "#0f172a",
  };
}

function agruparPorDia(logs) {
  const grupos = {};

  logs.forEach((log) => {
    const d = parseDate(log.arquivadoEm || log.criadoEm);

    let label = "Sem data";

    if (d) {
      label = d.toLocaleDateString("pt-PT", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
    }

    if (!grupos[label]) grupos[label] = [];
    grupos[label].push(log);
  });

  return grupos;
}

function ArchivedTimelineItem({ log, last, onRestore, onDelete, restoring, deleting }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const ui = getLogUi(log);
  const nome = obterNomeLog(log);
  const detalheExtra = obterDetalheValor(log);

  const clienteLink = log.clienteId ? `/admin/cliente/${log.clienteId}` : null;
  const empresaLink = log.empresaId ? `/admin/empresa/${log.empresaId}/info` : null;
  const detalheLink = clienteLink || empresaLink;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "38px 1fr", sm: "54px 1fr" },
        gap: 1.4,
      }}
    >
      <Box sx={{ position: "relative", display: "flex", justifyContent: "center" }}>
        {!last && (
          <Box
            sx={{
              position: "absolute",
              top: 46,
              bottom: -18,
              width: 2,
              borderRadius: 99,
              bgcolor: alpha(ui.color, isDark ? 0.24 : 0.16),
            }}
          />
        )}

        <Avatar
          sx={{
            width: 42,
            height: 42,
            borderRadius: "16px",
            bgcolor: alpha(ui.color, 0.12),
            color: ui.color,
            border: `1px solid ${alpha(ui.color, 0.22)}`,
            boxShadow: `0 12px 28px ${alpha(ui.color, 0.14)}`,
            zIndex: 1,
          }}
        >
          {ui.icon}
        </Avatar>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.7, md: 2 },
          borderRadius: "22px",
          bgcolor: isDark ? "rgba(255,255,255,0.035)" : "#ffffff",
          border: `1px solid ${alpha(ui.color, isDark ? 0.16 : 0.12)}`,
          borderLeft: `4px solid ${ui.color}`,
          transition: "0.22s ease",
          "&:hover": {
            transform: "translateY(-2px)",
            borderColor: alpha(ui.color, 0.28),
            boxShadow: isDark
              ? "0 18px 42px rgba(0,0,0,0.24)"
              : "0 18px 42px rgba(15,23,42,0.075)",
          },
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.5}>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography sx={{ fontWeight: 950 }}>
                {ui.title}
              </Typography>

              <Chip
                size="small"
                label="Arquivado"
                icon={<ArchiveIcon />}
                sx={{
                  height: 24,
                  borderRadius: 999,
                  fontWeight: 900,
                  bgcolor: isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9",
                  color: "text.secondary",
                }}
              />
            </Stack>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
              {nome}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.8, lineHeight: 1.65 }}>
              {log.detalhe || "Sem detalhe."}
            </Typography>

            {detalheExtra && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.6 }}>
                {detalheExtra}
              </Typography>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.6 }}>
              Criado em: {formatarData(log.criadoEm)} · Arquivado em: {formatarData(log.arquivadoEm)}
            </Typography>
          </Box>

          <Stack alignItems={{ xs: "flex-start", md: "flex-end" }} spacing={0.8} sx={{ flexShrink: 0 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 900, color: ui.color }}>
              {formatarHora(log.arquivadoEm || log.criadoEm)}
            </Typography>

            <Chip
              label={log.acao || "Atividade"}
              size="small"
              sx={{
                borderRadius: 999,
                fontWeight: 900,
                bgcolor: alpha(ui.color, 0.1),
                color: ui.color,
                maxWidth: 280,
              }}
            />

            {detalheLink && (
              <Button
                component={RouterLink}
                to={detalheLink}
                size="small"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 900,
                }}
              >
                Ver detalhes
              </Button>
            )}

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <Button
                variant="outlined"
                color="success"
                startIcon={<RestoreIcon />}
                disabled={restoring}
                onClick={() => onRestore(log)}
                sx={{
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 900,
                }}
              >
                {restoring ? "A restaurar..." : "Restaurar"}
              </Button>

              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteForeverIcon />}
                disabled={deleting}
                onClick={() => onDelete(log)}
                sx={{
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 900,
                }}
              >
                {deleting ? "A eliminar..." : "Eliminar"}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}

export default function AdminNotificacoesArquivadas() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pesquisa, setPesquisa] = useState("");
  const [restoringId, setRestoringId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const ui = {
    bg: isDark ? "#020617" : "#f6f8fc",
    card: isDark ? "rgba(15,23,42,0.86)" : "#ffffff",
    soft: isDark ? "rgba(255,255,255,0.04)" : "#f8fafc",
    border: isDark ? "rgba(255,255,255,0.09)" : "rgba(15,23,42,0.08)",
    text: theme.palette.text.primary,
    muted: theme.palette.text.secondary,
  };

  useEffect(() => {
    const q = query(
      collection(db, "atividadeLogs"),
      where("arquivado", "==", true),
      orderBy("criadoEm", "desc"),
      limit(150)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        setLogs(list);
        setLoading(false);
      },
      (error) => {
        console.error(error);
        setLoading(false);
      }
    );

    return unsub;
  }, []);

  const filtrados = useMemo(() => {
    const termo = normalize(pesquisa);

    if (!termo) return logs;

    return logs.filter((log) => {
      return (
        normalize(log.acao).includes(termo) ||
        normalize(log.titulo).includes(termo) ||
        normalize(log.detalhe).includes(termo) ||
        normalize(log.clienteNome).includes(termo) ||
        normalize(log.empresaNome).includes(termo) ||
        normalize(log.entidadeNome).includes(termo) ||
        normalize(log.referencia).includes(termo)
      );
    });
  }, [logs, pesquisa]);

  const agrupados = useMemo(() => agruparPorDia(filtrados), [filtrados]);

  async function restaurarLog(log) {
    try {
      setRestoringId(log.id);

      await updateDoc(doc(db, "atividadeLogs", log.id), {
        arquivado: false,
        restauradoEm: new Date(),
      });

      showSuccess("Atividade restaurada com sucesso.");
    } catch (error) {
      console.error(error);
      showError("Erro ao restaurar atividade.");
    } finally {
      setRestoringId(null);
    }
  }

  async function eliminarLog(log) {
    if (
      !window.confirm(
        "Eliminar permanentemente esta atividade?\n\nEsta ação não pode ser desfeita."
      )
    ) {
      return;
    }

    try {
      setDeletingId(log.id);

      await deleteDoc(doc(db, "atividadeLogs", log.id));

      showWarning("Atividade eliminada permanentemente.");
    } catch (error) {
      console.error(error);
      showError("Erro ao eliminar atividade.");
    } finally {
      setDeletingId(null);
    }
  }

  if (loading) {
    return (
      <Box sx={{ minHeight: "60vh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100%", bgcolor: ui.bg, p: { xs: 0.5, md: 1 } }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3.5 },
          borderRadius: "30px",
          mb: 3,
          overflow: "hidden",
          position: "relative",
          color: "#fff",
          background: isDark
            ? "linear-gradient(135deg,#020617,#0f172a,#1e1b4b)"
            : "linear-gradient(135deg,#0f172a,#1e3a8a,#312e81)",
          boxShadow: isDark
            ? "0 28px 80px rgba(0,0,0,0.32)"
            : "0 28px 80px rgba(15,23,42,0.16)",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            right: -90,
            top: -130,
            width: 380,
            height: 380,
            borderRadius: "50%",
            background: "rgba(148,163,184,0.22)",
          }}
        />

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={3}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          sx={{ position: "relative", zIndex: 1 }}
        >
          <Stack spacing={2}>
            <Button
              component={RouterLink}
              to="/admin/notificacoes"
              startIcon={<ArrowBackIcon />}
              variant="outlined"
              sx={{
                width: "fit-content",
                borderRadius: 999,
                textTransform: "none",
                fontWeight: 900,
                color: "#fff",
                borderColor: "rgba(255,255,255,0.28)",
                "&:hover": {
                  borderColor: "#fff",
                  bgcolor: "rgba(255,255,255,0.08)",
                },
              }}
            >
              Voltar às notificações
            </Button>

            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "22px",
                  bgcolor: "rgba(255,255,255,0.14)",
                  border: "1px solid rgba(255,255,255,0.18)",
                }}
              >
                <ArchiveIcon fontSize="large" />
              </Avatar>

              <Box>
                <Typography
                  sx={{
                    fontSize: { xs: "2rem", md: "3rem" },
                    fontWeight: 950,
                    letterSpacing: "-0.07em",
                    lineHeight: 1,
                  }}
                >
                  Atividades arquivadas
                </Typography>

                <Typography sx={{ mt: 1, color: "rgba(255,255,255,0.72)" }}>
                  Consulte, restaure ou elimine atividades antigas.
                </Typography>
              </Box>
            </Stack>
          </Stack>

          <Chip
            label={`${filtrados.length} arquivada(s)`}
            sx={{
              borderRadius: 999,
              fontWeight: 900,
              bgcolor: "rgba(255,255,255,0.12)",
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.18)",
            }}
          />
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: "26px",
          mb: 2.5,
          border: `1px solid ${ui.border}`,
          bgcolor: ui.card,
          boxShadow: isDark
            ? "0 18px 50px rgba(0,0,0,0.18)"
            : "0 18px 45px rgba(15,23,42,0.055)",
        }}
      >
        <TextField
          fullWidth
          value={pesquisa}
          onChange={(e) => setPesquisa(e.target.value)}
          placeholder="Pesquisar por ação, cliente, empresa, referência ou detalhe..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              height: 54,
              borderRadius: "18px",
              bgcolor: isDark ? "rgba(255,255,255,0.035)" : "#ffffff",
            },
          }}
        />
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: "28px",
          p: { xs: 2.2, md: 2.8 },
          bgcolor: ui.card,
          border: `1px solid ${ui.border}`,
          boxShadow: isDark
            ? "0 18px 55px rgba(0,0,0,0.18)"
            : "0 18px 45px rgba(15,23,42,0.055)",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.4 }}>
          <Avatar
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
              width: 52,
              height: 52,
              borderRadius: "18px",
            }}
          >
            <RadioButtonCheckedIcon />
          </Avatar>

          <Box>
            <Typography sx={{ fontWeight: 950, fontSize: "1.25rem" }}>
              Timeline arquivada
            </Typography>

            <Typography color="text.secondary">
              Histórico arquivado agrupado por data.
            </Typography>
          </Box>
        </Stack>

        {filtrados.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 5,
              borderRadius: "22px",
              bgcolor: ui.soft,
              border: `1px dashed ${ui.border}`,
              textAlign: "center",
            }}
          >
            <Avatar
              sx={{
                mx: "auto",
                mb: 2,
                width: 58,
                height: 58,
                borderRadius: "18px",
                bgcolor: isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9",
                color: "text.secondary",
              }}
            >
              <HistoryIcon />
            </Avatar>

            <Typography fontWeight={950}>
              Nenhuma atividade arquivada encontrada.
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Quando arquivares atividades, elas aparecem aqui.
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={3}>
            {Object.entries(agrupados).map(([dia, items]) => (
              <Box key={dia}>
                <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1.6 }}>
                  <Chip
                    label={dia}
                    sx={{
                      borderRadius: 999,
                      fontWeight: 950,
                      bgcolor: isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9",
                    }}
                  />
                  <Divider sx={{ flex: 1 }} />
                </Stack>

                <Stack spacing={1.8}>
                  {items.map((log, index) => (
                    <ArchivedTimelineItem
                      key={log.id}
                      log={log}
                      last={index === items.length - 1}
                      onRestore={restaurarLog}
                      onDelete={eliminarLog}
                      restoring={restoringId === log.id}
                      deleting={deletingId === log.id}
                    />
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Paper>
    </Box>
  );
}